/* app/foundations.jsx - Stage 1 Foundations reference + Nav state model.
   A doc-style screen that documents the Huddle tokens Marketlens inherits and
   the nav-item state model (default/hover/active/focus/disabled/badge/collapsed). */
(function () {
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }

  const COLORS = [
    { group: "Primary (indigo)", items: [
      ["--primary", "#4F46E5", true], ["--primary-hover", "#4338CA", true], ["--primary-active", "#3730A3", true],
      ["--primary-tint", "#EEF2FF"], ["--primary-tint-2", "#E0E7FF"],
    ]},
    { group: "Neutral ramp", items: [
      ["--gray-900", "#101828", true], ["--gray-700", "#344054", true], ["--gray-500", "#667085", true],
      ["--gray-300", "#D0D5DD"], ["--gray-200", "#E4E7EC"], ["--gray-100", "#F2F4F7"], ["--gray-75", "#F7F8FA"],
    ]},
    { group: "Status", items: [
      ["--success", "#15803D", true], ["--danger", "#B42318", true], ["--warning", "#B54708", true],
      ["--info", "#175CD3", true],
    ]},
    { group: "Marketlens additions", items: [
      ["--ml-draft", "#B54708", true], ["--ml-published", "#15803D", true],
      ["--ml-ai-surface", "#EEF2FF"], ["--ml-rel-look-fg", "#175CD3", true], ["--ml-rel-comp-fg", "#15803D", true], ["--ml-rel-alt-fg", "#B54708", true],
    ]},
  ];

  const TYPE = [
    ["31 / 38 · bold", "var(--text-2xl)", 700, "Display & hero metrics"],
    ["25 / 32 · semibold", "var(--text-xl)", 600, "Page titles"],
    ["20 / 28 · semibold", "var(--text-lg)", 600, "Card & section heads"],
    ["16 / 24 · regular", "var(--text-md)", 400, "Body and inputs"],
    ["14 / 20 · medium", "var(--text-sm)", 500, "Default UI text"],
    ["12 / 16 · medium", "var(--text-xs)", 500, "Captions, table meta, pills"],
  ];

  const SPACE = [["--space-1","4px"],["--space-2","8px"],["--space-3","12px"],["--space-4","16px"],["--space-5","20px"],["--space-6","24px"],["--space-8","32px"],["--space-10","40px"],["--space-12","48px"],["--space-16","64px"]];
  const RADII = [["--radius-xs","4px"],["--radius-sm","6px"],["--radius-md","8px"],["--radius-lg","12px"],["--radius-xl","16px"],["--radius-pill","999px"]];
  const ELEV = [["--shadow-xs","cards / inputs"],["--shadow-sm","dropdowns"],["--shadow-md","popovers"],["--shadow-lg","toasts"],["--shadow-xl","modals / drawers"]];

  function NavItemPreview({ icon, label, badge, state }) {
    const cls = ["hds-navitem"];
    if (state === "active") cls.push("hds-navitem--active");
    if (state === "hover") cls.push("is-hover");
    if (state === "focus") cls.push("is-focus");
    if (state === "disabled") cls.push("is-disabled");
    return (
      <a href="#" className={cls.join(" ")} onClick={(e) => e.preventDefault()}>
        <MlIcon name={icon} size={18} />
        <span>{label}</span>
        {badge != null && <span className="hds-navitem__badge">{badge}</span>}
      </a>
    );
  }

  // Shared header for the design-reference area (Foundations / Components tabs)
  function RefHeader({ view, onNav, onBack, eyebrow, title }) {
    const { Button } = window.HuddleDesignSystem_775113;
    return (
      <>
        <div className="ml-pagehead" style={{ marginBottom: 12 }}>
          <div className="ml-pagehead__titles">
            <div className="ml-doc__eyebrow">{eyebrow}</div>
            <h1 className="ml-doc__h1">{title}</h1>
          </div>
          <div className="ml-pagehead__actions">
            <Button variant="secondary" leadingIcon="chevron-left" onClick={onBack}>Back to app</Button>
          </div>
        </div>
        <div className="hds-tabs" style={{ marginBottom: 24 }}>
          <button className={"hds-tab " + (view === "foundations" ? "hds-tab--active" : "")} onClick={() => onNav("foundations")}>Foundations</button>
          <button className={"hds-tab " + (view === "components" ? "hds-tab--active" : "")} onClick={() => onNav("components")}>Components</button>
        </div>
      </>
    );
  }
  window.MlRef = { RefHeader };

  function Foundations({ onBack, onNav }) {
    const { Button, Badge } = window.HuddleDesignSystem_775113;
    return (
      <div className="ml-doc">
        <RefHeader view="foundations" onNav={onNav} onBack={onBack} eyebrow="Stage 1 · Foundations" title="Marketlens design foundations" />
        <p className="ml-doc__lede">Marketlens is built directly on the Huddle Design System - light theme, one indigo accent, system fonts, a 4px grid and soft hairline-bordered cards. These are the exact tokens every screen inherits. Marketlens-specific additions (draft lifecycle, AI accent, relationship tokens) are derived from Huddle's base palette and flagged as proposed system additions.</p>

        {/* Color */}
        <section className="ml-doc__section">
          <h2>Color</h2>
          <p className="ml-doc__note">One accent (indigo) carries primary actions, active nav and focus. Neutrals do the heavy lifting; status hues stay reserved and meaningful. Indigo is never used for “success”.</p>
          {COLORS.map(c => (
            <div key={c.group} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-secondary)", marginBottom: 10 }}>{c.group}</div>
              <div className="ml-swatchgrid">
                {c.items.map(([name, val, dark]) => (
                  <div className="ml-swatch" key={name}>
                    <div className="ml-swatch__chip" style={{ background: val, borderBottom: "1px solid var(--border-subtle)" }} />
                    <div className="ml-swatch__meta">
                      <div className="ml-swatch__name">{name}</div>
                      <div className="ml-swatch__val">{val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Type */}
        <section className="ml-doc__section">
          <h2>Typography</h2>
          <p className="ml-doc__note">System-font stack - no webfont download. Six-step scale: 12 / 14 / 16 / 20 / 25 / 31. 14px is the default UI size; metrics use tabular numerals and tight tracking.</p>
          <div className="hds-card hds-card--pad">
            {TYPE.map(([meta, size, weight, use]) => (
              <div className="ml-typerow" key={meta}>
                <div className="ml-typerow__meta">{meta}<br />{use}</div>
                <div className="ml-typerow__sample" style={{ fontSize: size, fontWeight: weight, letterSpacing: weight >= 600 ? "-0.02em" : 0 }}>Content operations at scale</div>
              </div>
            ))}
          </div>
        </section>

        {/* Spacing + radius */}
        <section className="ml-doc__section">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div>
              <h2>Spacing</h2>
              <p className="ml-doc__note">Strict 4px grid.</p>
              <div className="hds-card hds-card--pad">
                {SPACE.map(([name, val]) => (
                  <div className="ml-tokenrow" key={name}>
                    <span className="ml-tokenrow__name">{name}</span>
                    <span className="ml-tokenrow__bar" style={{ width: val }} />
                    <span className="ml-tokenrow__val">{val}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2>Radius</h2>
              <p className="ml-doc__note">8 controls · 12 cards · 999 pills.</p>
              <div className="hds-card hds-card--pad">
                {RADII.map(([name, val]) => (
                  <div className="ml-tokenrow" key={name}>
                    <span style={{ width: 44, height: 32, background: "var(--primary-tint)", border: "1px solid var(--indigo-200)", borderRadius: val, flex: "0 0 auto" }} />
                    <span className="ml-tokenrow__name">{name}</span>
                    <span className="ml-tokenrow__val">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Elevation */}
        <section className="ml-doc__section">
          <h2>Elevation</h2>
          <p className="ml-doc__note">Soft, low-contrast, ink-tinted shadows. Cards lean on border + xs, not heavy drop shadows. No colored shadows, no gradients.</p>
          <div className="ml-elevrow">
            {ELEV.map(([name, use]) => (
              <div className="ml-elevtile" key={name} style={{ boxShadow: `var(${name})` }}>{name.replace("--shadow-","")} · {use}</div>
            ))}
          </div>
        </section>

        {/* Nav state model */}
        <section className="ml-doc__section">
          <h2>Navigation state model</h2>
          <p className="ml-doc__note">Every sidebar nav item resolves to one of these states. Hover and focus are shown statically here for documentation.</p>
          <div className="hds-card hds-card--pad" style={{ maxWidth: 560 }}>
            <div className="ml-stategrid">
              <div className="ml-stategrid__label">default</div>
              <div className="ml-statebox"><NavItemPreview icon="file-text" label="Posts" /></div>
              <div className="ml-stategrid__label">hover</div>
              <div className="ml-statebox"><NavItemPreview icon="file-text" label="Posts" state="hover" /></div>
              <div className="ml-stategrid__label">active</div>
              <div className="ml-statebox"><NavItemPreview icon="file-text" label="Posts" state="active" /></div>
              <div className="ml-stategrid__label">focus</div>
              <div className="ml-statebox"><NavItemPreview icon="file-text" label="Posts" state="focus" /></div>
              <div className="ml-stategrid__label">disabled</div>
              <div className="ml-statebox"><NavItemPreview icon="file-text" label="Posts" state="disabled" /></div>
              <div className="ml-stategrid__label">with badge</div>
              <div className="ml-statebox"><NavItemPreview icon="inbox" label="Inbox" badge={5} /></div>
              <div className="ml-stategrid__label">active + badge</div>
              <div className="ml-statebox"><NavItemPreview icon="inbox" label="Inbox" badge={5} state="active" /></div>
            </div>
          </div>
        </section>

        {/* Status & relationship tokens preview */}
        <section className="ml-doc__section">
          <h2>Status &amp; relationship tokens</h2>
          <p className="ml-doc__note">Reserved, meaningful pills. Status never relies on color alone - each pairs a dot or icon with a label.</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <Badge tone="success" dot>Published</Badge>
            <span className="hds-badge hds-badge--draft"><span className="hds-badge__dot" />Draft</span>
            <span className="hds-badge hds-badge--info"><span className="hds-badge__dot" />In review</span>
            <Badge tone="warning" dot>Pending</Badge>
            <span className="hds-badge hds-badge--ai"><MlIcon name="sparkles" size={12} />AI-assisted</span>
            <span className="hds-badge hds-badge--rel-look">Lookalike</span>
            <span className="hds-badge hds-badge--rel-comp">Complementary</span>
            <span className="hds-badge hds-badge--rel-alt">Alternative</span>
            <span className="ml-ours"><svg viewBox="0 0 24 24"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>Ours</span>
          </div>
        </section>
      </div>
    );
  }

  window.MlFoundations = { Foundations };
})();
